import { createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react';
import { useParams } from 'react-router-dom';

// Define the interface for the context value
interface DesignStudioContextType {
  fieldId: number;
  setFieldId: (id: number) => void;
  documentTypeId: number;
  setDocumentTypeId: (id: number) => void;
  graphId: string;
  setGraphId: (id: string) => void;
  graphState: any;
  SetgraphState: (id: any) => void;
  processId: number;
  setprocessID: (id: number) => void;
  canvashistory: { canRedo: boolean; canUndo: boolean };
  setCanvashistory: (canRedo: boolean, canUndo: boolean) => void;
  fieldIdRef: React.MutableRefObject<number>;
  setZoomIndex: (id: number) => void;
  zoomIndex: number;
  isLoading: boolean;
  setIsLoading: (id: boolean) => void;
}

// Define the context with the correct type or use `null` as an initial value
const DesignStudioContext = createContext<DesignStudioContextType | null>(null);

// Define the props type for the WorkflowProvider
interface DesignStudioProviderProps {
  children: ReactNode;
}

// Create the provider component
export const DesignStudioProvider = ({ children }: DesignStudioProviderProps) => {
  const { processID, fieldID, docTypeID, graph } = useParams<{
    processID: string;
    fieldID: string;
    docTypeID: string;
    graph: any;
  }>();

  const [fieldId, setFieldId] = useState<number>(Number(fieldID));
  const fieldIdRef = useRef<number>(fieldId); // Use useRef for fieldId
  const [documentTypeId, setDocumentTypeId] = useState<number>(Number(docTypeID));
  const [processId, setprocessID] = useState<number>(Number(processID));
  const [graphId, setGraphId] = useState<string>('graph-container');
  const [graphState, SetgraphState] = useState(graph);
  const [canvashistory, updateCanvashistory] = useState({ canRedo: false, canUndo: false });
  const [zoomIndex, setZoomIndex] = useState<number>(100);
  const [isLoading, setIsLoading] = useState(false);
  const setCanvashistory = (canRedo: boolean, canUndo: boolean) => {
    updateCanvashistory({ canRedo, canUndo });
  };

  // Keep fieldIdRef in sync with fieldId
  useEffect(() => {
    fieldIdRef.current = fieldId;
  }, [fieldId]);

  return (
    <DesignStudioContext.Provider
      value={{
        fieldId,
        setFieldId,
        documentTypeId,
        setDocumentTypeId,
        graphId,
        setGraphId,
        graphState,
        SetgraphState,
        processId,
        setprocessID,
        canvashistory,
        setCanvashistory,
        fieldIdRef,
        setZoomIndex,
        zoomIndex,
        isLoading,
        setIsLoading
      }}
    >
      {children}
    </DesignStudioContext.Provider>
  );
};

// Custom hook to use the WorkflowContext
export const useWorkflowContext = () => {
  const context = useContext(DesignStudioContext);
  if (!context) {
    throw new Error('useWorkflowContext must be used within a WorkflowProvider');
  }

  return context;
};
